/* eslint-disable consistent-return */
/* eslint-disable no-secrets/no-secrets */

import React, { useContext, useState, useEffect, useCallback } from 'react';
import _ from 'lodash';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { ViewModelForm, ViewModelServiceContext, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';

import {
    WMICRichTextUtil,
    WMICJurisdictionUtil,
    WMICDateTimeService,
    JURISDICTIONS,
    CONSTANTS,
    MODAL_CONSTANTS,
    JobType,
    WMICLogger,
    parseErrorMessage,
} from 'wmic-pe-portals-utils-js';
import PropTypes from 'prop-types';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { WMICAddLossHistoryComponent } from 'wmic-pe-capability-gateway-quoteandbind-pa-react';
import cx from 'classnames';

import { UISupportingInfoLookupService } from 'wmic-pe-capability-supportinginfo';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useWizardModals } from 'wmic-pe-portals-wizard-components-ui';
import { WMICErrorHandler } from 'wmic-pe-capability-quoteandbind-common-react';

import { CheckboxField } from '@jutro/legacy/components';
import { DropdownMenuLink } from '@jutro/router';

import { RISK_PATHS } from 'wmic-pe-portals-utils-js/StringConstants';
import { WMICButton } from 'wmic-pe-components-platform-react';
import styles from './WMICPALossHistoryComponent.module.scss';
import metadata from './WMICPALossHistoryComponent.metadata.json5';
import messages from './WMICPALossHistoryComponent.messages';
import { TranslatorContext } from '@jutro/legacy/locale';

let carriersList;

function WMICPALossHistoryComponent(props) {
    const {
        id,
        insuranceHistoryVM,
        updateWizardData,
        submissionVM,
        showErrors,
        isReadOnlyUser,
        isEditMode,
        baseData,
        onValidate,
        authHeader,
    } = props;

    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const { onValidate: setComponentValidation, isComponentValid } = useValidation(id);
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { setWizardLoading, showConfirm } = useWizardModals();
    const { refreshData } = useDataRefresh();

    const [shouldWait, setShouldWait] = useState(false);
    const [selectedDriversForFCSA, setSelectedDriversForFCSA] = useState([]);
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [vehicleOperators, setVehicleOperators] = useState([]);

    const availableDrivers = _.get(submissionVM, `${RISK_PATHS.DRIVERS_PATH}.value`);
    const availableVehicles = _.get(submissionVM, `${RISK_PATHS.VEHICLES_PATH}.value`);
    const isPolicyChangeOrRenewal =
        _.get(submissionVM, 'baseData.jobType.value.code') === JobType.POLICY_CHANGE ||
        _.get(submissionVM, 'baseData.jobType.value.code') === JobType.RENEWAL;

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
        return () => (onValidate ? onValidate(true, id) : undefined);
    }, [isComponentValid, onValidate, id]);

    const isPolicyChange = () => {
        const transactionType = _.get(baseData, 'jobType.code');
        return transactionType === 'PolicyChange';
    };

    const hasNewDrivers = _.some(availableDrivers, driver => !driver.isExistingInPreviousPeriodDriver_WMIC);

    useEffect(() => {
        let driversForVehicle;

        if (isPolicyChange() && hasNewDrivers) {
            driversForVehicle = _.filter(availableDrivers, driver => {
                if (!driver.isExistingInPreviousPeriodDriver) {
                    return driver;
                }
            });
        } else {
            driversForVehicle = availableDrivers;
        }

        _.each(driversForVehicle, driver => {
            vehicleOperators.push({
                code: driver.person?.displayName,
                name: driver.person?.displayName,
            });
        });

        vehicleOperators.push({
            code: 'reportedDriver',
            name: translator(messages.reportedDriver),
        });

        UISupportingInfoLookupService.retrieveInsuranceCarriersList(
            [baseData.baseState.value.code, baseData.periodStartDate.value],
            true,
            authHeader
        ).then(carriers => {
            carriers.forEach(item => {
                // eslint-disable-next-line no-param-reassign
                item.name = item.carrier;
                // eslint-disable-next-line no-param-reassign
                item.code = item.carrier;
            });
            carriersList = carriers;
        });

        setVehicleOperators(vehicleOperators);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isBaseStateQC = useCallback(
        () => WMICJurisdictionUtil.isBaseState(_.get(submissionVM, 'baseData'), JURISDICTIONS.QUEBEC),
        [submissionVM]
    );

    useEffect(() => {
        if (insuranceHistoryVM?.value?.fcsaProvided === undefined && hasNewDrivers && isBaseStateQC()) {
            insuranceHistoryVM.value.fcsaProvided = false;
            refreshData();
        }
    }, [hasNewDrivers, insuranceHistoryVM, isBaseStateQC, refreshData]);

    const addNewPriorLoss = async () => {
        const priorLoss = viewModelService.create(
            {
                isReadOnly: false,
                isManualEntryLoss: true,
                payments: []
            },
            'pc',
            'wmic.edge.ca.capabilities.policyjob.lob.common.draft.dto.insurancehistory.PriorLoss_WMICDTO',
            submissionVM.aspects.context()
        );

        priorLoss.value.paPriorLoss = viewModelService.create(
            { vehicleOperator: 'reportedDriver' },
            'pc',
            'wmic.edge.ca.capabilities.policyjob.lob.common.draft.dto.insurancehistory.PaPriorLoss_WMICDTO',
            { ...submissionVM.aspects.context(), IsReadOnly: false }
        ).value;

        insuranceHistoryVM.priorLosses.value.push(priorLoss.value);
        return priorLoss;
    };

    const resetChargeableAndUseForRatingData = priorLoss => {
        _.set(priorLoss, 'paPriorLoss.value.chargeable', null);
        _.set(priorLoss, 'paPriorLoss.value.useForRating', null);
    };

    const savePriorLoss = priorLoss => {
        resetChargeableAndUseForRatingData(priorLoss);
        return true;
    };

    const removePriorLoss = (item, index) => {
        showConfirm({
            title: messages.removeLossHistoryTitle,
            message: translator(messages.removeLossHistoryMsg),
            status: MODAL_CONSTANTS.STATUS.WARNING,
            icon: MODAL_CONSTANTS.ICON.WARNING,
            confirmButtonText: messages.yes,
            cancelButtonText: messages.no,
        }).then(result => {
            if (result === CONSTANTS.MODAL_RESULT.CONFIRM) {
                const priorLosses = _.get(insuranceHistoryVM, 'priorLosses.value');
                priorLosses.splice(index, 1);
                refreshData();
            }
        });
    };

    const isFCSAOrderSuccessForAllDrivers = () => {
        const driversCount = _.get(submissionVM, `${RISK_PATHS.DRIVERS_PATH}.value`, []).length;
        const successCount = _.countBy(_.get(insuranceHistoryVM, 'fcsaOrderedLossHistory.value'), {
            fcsaOrderStatus: 'Received',
        }).true;

        return driversCount === successCount;
    };

    const isPartialFailure = () => {
        const driversCount = _.get(submissionVM, `${RISK_PATHS.DRIVERS_PATH}.value`, []).length;
        const errorCount = _.countBy(_.get(insuranceHistoryVM, 'fcsaOrderedLossHistory.value'), {
            fcsaOrderStatus: 'Error',
        }).true;

        return driversCount > errorCount;
    };

    const isFullFailure = () => {
        const driversCount = _.get(submissionVM, `${RISK_PATHS.DRIVERS_PATH}.value`, []).length;
        const errorCount = _.countBy(_.get(insuranceHistoryVM, 'fcsaOrderedLossHistory.value'), {
            fcsaOrderStatus: 'Error',
        }).true;

        return driversCount === errorCount;
    };

    const getOrderedLossHistoryFCSA = driver => {
        // need middle name to match pc data, currently driver.displayName does not include the middle name
        const driverIncMiddleName = driver.person.displayName;
        return _.find(insuranceHistoryVM.fcsaOrderedLossHistory?.value, { requestedDriver: driverIncMiddleName });
    };

    const isSuccess = driver => getOrderedLossHistoryFCSA(driver)?.fcsaOrderStatus === 'Received';

    const isError = driver => getOrderedLossHistoryFCSA(driver)?.fcsaOrderStatus === 'Error';

    const isRequired = driver => _.isUndefined(getOrderedLossHistoryFCSA(driver));

    const getDriver = data => {
        const driverName = data.value.paPriorLoss.vehicleOperator;
        const drivers = _.get(submissionVM, `${RISK_PATHS.DRIVERS_PATH}.value`);

        const driver = _.find(drivers, currentDriver => currentDriver.person?.displayName === driverName);

        return driver
            ? `#${driver.driverNumber_WMIC}: ${driver.person?.displayName}`
            : `#:${translator(messages.reportedDriver)}`;
    };

    const getAtFaultDisplayName = data =>
        translator({
            id: `typekey.AtFaultIndicator_WMIC.${data.value.paPriorLoss.atFault}`,
        });

    const localDate2Date = data => WMICDateTimeService.toMidnightDate(data.lossDate.value);

    const getClaimNumber = data => data.value.claimNumber;

    const getPolicyNumber = data => data.value.policyNum;

    const getDriverName = driver => {
        const fcsaOrderedLossHistory = getOrderedLossHistoryFCSA(driver);

        if (fcsaOrderedLossHistory?.fcsaOrderStatus) {
            if (fcsaOrderedLossHistory.fcsaOrderStatus === 'Error') {
                return translator(messages.FCSADriverNameError, {
                    driverName: driver.displayName,
                    date: WMICDateTimeService.toMidnightDate(fcsaOrderedLossHistory.dateFCSAOrdered),
                });
            }

            if (fcsaOrderedLossHistory.fcsaOrderStatus === 'Received') {
                return translator(messages.FCSADriverNameSuccess, {
                    driverName: driver.displayName,
                    date: WMICDateTimeService.toMidnightDate(fcsaOrderedLossHistory.dateFCSAOrdered),
                });
            }
        } else if (isPolicyChange()) {
            return translator(messages.FCSADriverNameSuccess, { driverName: driver.displayName });
        }

        return driver.displayName;
    };

    const getFCSAmsg = () => {
        if (!isPolicyChange()) {
            if (!isPartialFailure() && !isFullFailure()) {
                return translator(messages.FCSAFailure);
            }

            if (isPartialFailure()) {
                return translator(messages.FCSAPartialFailure);
            }

            if (isFullFailure()) {
                return WMICRichTextUtil.translateRichText(translator(messages.FCSAFullFailure));
            }
        } else {
            return translator(messages.FCSAFailurePolicyChanged);
        }
    };

    const getFCSAdrivers = () => {
        const drivers = _.get(submissionVM, `${RISK_PATHS.DRIVERS_PATH}.value`);
        const fcsaProvided = _.get(insuranceHistoryVM, 'fcsaProvided.value');

        return drivers?.map(driver => (
            <div className={styles.fcsaDriverContainer}>
                {isSuccess(driver) && !isError(driver) && !fcsaProvided && (
                    <div className={cx(styles.badge, styles.badgeApproved)}>
                        <i className="mir mi-check" />
                        {translator(messages.FCSASuccess)}
                    </div>
                )}
                {isRequired(driver) && !shouldWait && !fcsaProvided && (
                    <div className={cx(styles.badge, styles.badgeRequired)}>
                        <i className="fa fa-fw fa-asterisk" />
                        {translator(messages.FCSARequired)}
                    </div>
                )}
                {fcsaProvided && (
                    <div className={cx(styles.badge, styles.badgeNotRequested)}>
                        <i className="mir mi-remove" />
                        {translator(messages.FCSANotRequested)}
                    </div>
                )}
                {shouldWait && (
                    <div className={cx(styles.badge, styles.badgeWait)}>
                        <i className="fa fa-fw fa-spinner" />
                        {translator(messages.FCSAWait)}
                    </div>
                )}
                {!isSuccess(driver) && isError(driver) && !fcsaProvided && (
                    <div className={cx(styles.badge, styles.badgeError)}>
                        <i className="mir mi-close" />
                        {translator(messages.FCSAError)}
                    </div>
                )}
                <span className={isError(driver) && styles.fcsaError}>{getDriverName(driver)}</span>
            </div>
        ));
    };

    const getSelectedDriverID = driver =>
        _.find(selectedDriversForFCSA, selectedDriver => selectedDriver.publicID === driver.publicID);

    const handleDriverClick = driver => {
        setDropdownOpen(true);

        if (selectedDriversForFCSA.includes(driver)) {
            const driverIndex = selectedDriversForFCSA.indexOf(driver);

            setSelectedDriversForFCSA(prevState =>
                _.filter(prevState, (selectedDriver, selectedDriverIndex) => selectedDriverIndex !== driverIndex)
            );
        } else {
            setSelectedDriversForFCSA(prevState => [...prevState, driver]);
        }
    };

    const getAvailableDrivers = () => {
        const drivers = _.get(submissionVM, `${RISK_PATHS.DRIVERS_PATH}.value`, []);

        return drivers.map(driver => (
            <DropdownMenuLink className={styles.driversForFCSA} onMenuItemClick={() => handleDriverClick(driver)}>
                <CheckboxField
                    controlClassName={styles.driversCheckbox}
                    onValueChange={() => handleDriverClick(driver)}
                    label={driver.displayName}
                    showInlineLabel
                    value={driver.publicID === getSelectedDriverID(driver)?.publicID}
                />
            </DropdownMenuLink>
        ));
    };

    const orderFCSAForSelectedDrivers = async () => {
        if (selectedDriversForFCSA.length > 0) {
            setShouldWait(true);

            const transactionID =
                _.get(baseData, 'jobType.value.code') === JobType.SUBMISSION
                    ? submissionVM.quoteID.value
                    : submissionVM.jobID.value;
            const listOfSelectedPublicId = selectedDriversForFCSA.map(driver => driver.publicID);
            try {
                setWizardLoading(true);
                const result = await LoadSaveService.orderFCSAForDrivers(
                    transactionID,
                    listOfSelectedPublicId,
                    authHeader
                );
                if (result) {
                    if (result.errorMessages && result.errorMessages.length > 0) {
                        WMICErrorHandler.processAsModal(result.errorMessages.join('\n')).catch(_.noop);
                    }

                    _.set(
                        insuranceHistoryVM,
                        'fcsaOrderedLossHistory.value',
                        result.insuranceHistory.fcsaOrderedLossHistory
                    );
                    _.set(insuranceHistoryVM, 'priorLosses.value', result.insuranceHistory.priorLosses);
                }

                updateWizardData(submissionVM);
            } catch (err) {
                WMICLogger.error('Error ordering loss history', err);
                WMICErrorHandler.processAsModal(parseErrorMessage(err)).catch(_.noop);
            } finally {
                setWizardLoading(false);
            }

            setShouldWait(false);
        }
    };

    const fcsaDropDownToggled = () => {
        let duplicateCallDrivers = '';

        setDropdownOpen(false);

        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < insuranceHistoryVM.fcsaOrderedLossHistory?.value.length; i++) {
            // eslint-disable-next-line no-plusplus
            for (let j = 0; j < selectedDriversForFCSA.length; j++) {
                if (
                    selectedDriversForFCSA[j].displayName ===
                        insuranceHistoryVM.fcsaOrderedLossHistory?.value[i].requestedDriver &&
                    insuranceHistoryVM.fcsaOrderedLossHistory?.value[i].fcsaOrderStatus === 'Received'
                ) {
                    duplicateCallDrivers =
                        duplicateCallDrivers === ''
                            ? selectedDriversForFCSA[j].displayName
                            : `${duplicateCallDrivers}, ${selectedDriversForFCSA[j].displayName}`;
                }
            }
        }

        if (duplicateCallDrivers) {
            const modalMsg = translator(messages.fcsaConfirmationMsg, { driverName: duplicateCallDrivers });

            showConfirm({
                title: messages.fcsaConfirmationTitle,
                message: modalMsg,
            })
                .then(res => {
                    if (res === CONSTANTS.MODAL_RESULT.CONFIRM) {
                        orderFCSAForSelectedDrivers();
                    }
                })
                .catch(_.noop);
        } else {
            orderFCSAForSelectedDrivers();
        }
    };

    const handleFCSADriverBtnClick = e => {
        e.stopPropagation();
        setDropdownOpen(!isDropdownOpen);
    };

    const getChargeableMessage = data => {
        const chargeable = _.get(data, 'paPriorLoss.value.chargeable');

        if (_.isNil(chargeable)) {
            return translator(messages.pending);
        }

        return chargeable ? translator(messages.yes) : translator(messages.no);
    };

    const getUsedForRatingMessage = data => {
        const useForRating = _.get(data, 'paPriorLoss.value.useForRating');

        if (_.isNil(useForRating)) {
            return translator(messages.pending);
        }

        return useForRating ? translator(messages.yes) : translator(messages.no);
    };

    const getOverrideRatingDisplayName = data =>
        data?.paPriorLoss?.ratingReason?.value ? commonMessages.yesModel : commonMessages.noModel;

    const isRatingReasonVisible = data => !!data?.paPriorLoss?.ratingReason?.value;

    const canShowEditPriorLossButton = (index, data) => {
        const isFCSASource = _.get(data, 'sourceType.value.code') === 'fcsa';
        return (data?.value?.isReadOnly === false || data?.value?.isManualEntryLoss === true) && !isFCSASource;
    };

    const canShowDeletePriorLossButton = (index, data) => data?.value?.isReadOnly === false;

    const canShowAddLossButton = () => !isPolicyChangeOrRenewal || hasNewDrivers;

    const renderAddPriorLossButton = ({isEditing, onClick}) =>
        canShowAddLossButton() && (
            <WMICButton
                id="addLossButton"
                type={isBaseStateQC() ? 'secondary-outlined' : 'secondary'}
                onClick={onClick}
                disabled={isEditing}
                icon="mi-add"
                className="gw-mb-3"
            >
                {translator(messages.addLossButton)}
            </WMICButton>
        );

    const overrideProps = {
        '@field': {
            parentNode: insuranceHistoryVM,
            readOnly: !isEditMode || isReadOnlyUser,
        },
        noPriorLossesContainer: {
            visible: _.get(insuranceHistoryVM, 'priorLosses.value', []).length === 0,
        },
        insuranceLossHistoryListView: {
            value: _.get(insuranceHistoryVM, 'priorLosses.children', []),
            clickable: true,
            startOpen: false,
            VMData: [
                {
                    headerText: translator(messages.driverName),
                    getData: getDriver,
                    classNameColumn: styles.headerBottom,
                },
                {
                    headerText: isBaseStateQC() ? translator(messages.claimNumber) : translator(messages.policyNumber),
                    getData: isBaseStateQC() ? getClaimNumber : getPolicyNumber,
                    classNameColumn: styles.headerTop,
                },
                {
                    headerText: translator(messages.atFault),
                    getData: getAtFaultDisplayName,
                },
                {
                    headerText: translator(messages.lossDate),
                    getData: localDate2Date,
                },
                {
                    headerText: translator(messages.vehicleChargeable),
                    getData: getChargeableMessage,
                },
                {
                    headerText: translator(messages.vehicleUseForRating),
                    getData: getUsedForRatingMessage,
                },
                {
                    headerText: translator(messages.vehicleRatingOverrideApplied),
                    getData: getOverrideRatingDisplayName,
                },
                {
                    headerText: translator(messages.vehicleRatingReason),
                    path: 'paPriorLoss.ratingReason',
                    visibilityCondition: isRatingReasonVisible,
                },
            ],
            toCreate: addNewPriorLoss,
            onSave: savePriorLoss,
            onDelete: removePriorLoss,
            toUndoCreate: () => {
                const priorLosses = insuranceHistoryVM?.value?.priorLosses;
                priorLosses.splice(priorLosses.length - 1, 1);
                _.set(insuranceHistoryVM.value, 'priorLosses', priorLosses);
            },
            detailViewComponent: WMICAddLossHistoryComponent,
            onValidate,
            renderAddButton: renderAddPriorLossButton,
            detailViewComponentProps: {
                insuranceHistoryVM,
                submissionVM,
                carriersList,
                vehicleOperators,
                availableVehicles,
            },
            flatCards: true,
            canEdit: canShowEditPriorLossButton,
            canDelete: canShowDeletePriorLossButton,
            readOnly: !isEditMode || isReadOnlyUser,
        },
        FCSALossHistoryQCSectionContainer: {
            visible: isBaseStateQC(),
        },
        fcsaFailureMsg: {
            content: getFCSAmsg(),
        },
        fcsaFailureMsgContainer: {
            visible: !isFCSAOrderSuccessForAllDrivers() && !_.get(insuranceHistoryVM, 'fcsaProvided.value'),
        },
        fcsaDriversContainer: {
            content: getFCSAdrivers(),
        },
        driversFCSACheck: {
            data: availableDrivers,
        },
        availableDriversForFCSA: {
            content: getAvailableDrivers(),
        },
        selectedDriversForFCSA: {
            menuClassName: isDropdownOpen && styles.driversDropdownMenu,
        },
        driversForFCSAContainer: {
            onClick: event => handleFCSADriverBtnClick(event),
        },
        getDriversButton: {
            disabled: selectedDriversForFCSA.length === 0,
        },
    };

    const resolvers = {
        resolveCallbackMap: {
            fcsaDropDownToggled,
        },
        resolveClassNameMap: styles,
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={insuranceHistoryVM}
            overrideProps={overrideProps}
            onModelChange={() => refreshData()}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
            onValidationChange={setComponentValidation}
            showErrors={showErrors}
        />
    );
}

WMICPALossHistoryComponent.propTypes = {
    id: PropTypes.string.isRequired,
    insuranceHistoryVM: PropTypes.shape({}),
    submissionVM: PropTypes.shape({}),
    isEditMode: PropTypes.bool.isRequired,
    onValidate: PropTypes.func.isRequired,
    baseData: PropTypes.shape({}).isRequired,
    authHeader: PropTypes.shape({
        Authorization: PropTypes.string.isRequired,
    }).isRequired,
    isReadOnlyUser: PropTypes.bool.isRequired,
    showErrors: PropTypes.bool.isRequired,
};

WMICPALossHistoryComponent.defaultProps = {
    insuranceHistoryVM: {},
};

export default WMICPALossHistoryComponent;
